import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { User, UsersState, AddUserRequest, UpdateUserRequest } from '@/types/user.types';
import { UserRequestType } from '@/types/user.types';
import type { ListParams } from '@/types/api.types';

const initialState: UsersState = {
  userList: [],
  totalUsers: 0,
  loading: false,
  error: null,
  requestType: '',
  addUser: { loading: false, error: null },
  updateUser: { loading: false, error: null },
  emailExist: false,
};

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    // Fetch users
    fetchUsersRequest(state, _action: PayloadAction<ListParams>) {
      state.loading = true;
      state.error = null;
    },
    fetchUsersSuccess(state, action: PayloadAction<{ users: User[]; total: number }>) {
      state.loading = false;
      state.userList = action.payload.users;
      state.totalUsers = action.payload.total;
    },
    fetchUsersFailure(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    // Add user
    addUserRequest(state, _action: PayloadAction<AddUserRequest>) {
      state.addUser = { loading: true, error: null };
    },
    addUserSuccess(state) {
      state.addUser = { loading: false, error: null };
      state.requestType = UserRequestType.ADD_USER_SUCCESS;
    },
    addUserFailure(state, action: PayloadAction<string>) {
      state.addUser = { loading: false, error: action.payload };
    },

    // Update user
    updateUserRequest(state, _action: PayloadAction<UpdateUserRequest>) {
      state.updateUser = { loading: true, error: null };
    },
    updateUserSuccess(state) {
      state.updateUser = { loading: false, error: null };
      state.requestType = UserRequestType.UPDATE_USER_SUCCESS;
    },
    updateUserFailure(state, action: PayloadAction<string>) {
      state.updateUser = { loading: false, error: action.payload };
    },

    // Delete user
    deleteUserRequest(state, _action: PayloadAction<string>) {
      state.loading = true;
    },
    deleteUserSuccess(state, action: PayloadAction<string>) {
      state.loading = false;
      state.userList = state.userList.filter((u) => u._id !== action.payload);
      state.totalUsers -= 1;
      state.requestType = UserRequestType.DELETE_USER_SUCCESS;
    },
    deleteUserFailure(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    // Email exist check
    checkEmailExistRequest(state, _action: PayloadAction<string>) {
      state.emailExist = false;
    },
    checkEmailExistSuccess(state, action: PayloadAction<boolean>) {
      state.emailExist = action.payload;
    },

    // Reset request type
    resetRequestType(state) {
      state.requestType = '';
    },
  },
});

export const {
  fetchUsersRequest,
  fetchUsersSuccess,
  fetchUsersFailure,
  addUserRequest,
  addUserSuccess,
  addUserFailure,
  updateUserRequest,
  updateUserSuccess,
  updateUserFailure,
  deleteUserRequest,
  deleteUserSuccess,
  deleteUserFailure,
  checkEmailExistRequest,
  checkEmailExistSuccess,
  resetRequestType,
} = usersSlice.actions;

export default usersSlice.reducer;
